<template>
  <div class="editable-field">
    <div v-if="isEditing" class="editing-cell">
      <input 
        ref="editInput"
        v-model="editValue" 
        @blur="handleSave"
        @keyup.enter="handleSave"
        @keyup.esc="handleCancel"
        class="edit-input"
        type="text"
      />
    </div>
    <div v-else @click="handleStartEdit" class="editable-cell">
      {{ value }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch } from 'vue';

// Props
interface Props {
  value: string;
  isEditing: boolean;
}

const props = defineProps<Props>();

// Emits
interface Emits {
  'start-edit': [];
  'save': [value: string];
  'cancel': [];
}

const emit = defineEmits<Emits>();

// Reactive state
const editInput = ref<HTMLInputElement | null>(null);
const editValue = ref<string>(props.value);

// Watch for prop changes
watch(() => props.value, (newValue) => {
  editValue.value = newValue;
});

watch(() => props.isEditing, (isEditing) => {
  if (isEditing) {
    nextTick(() => {
      if (editInput.value) {
        editInput.value.focus();
        editInput.value.select();
      }
    });
  }
});

// Methods
const handleStartEdit = (): void => {
  emit('start-edit');
};

const handleSave = (): void => {
  emit('save', editValue.value);
};

const handleCancel = (): void => {
  editValue.value = props.value; // Reset to original value
  emit('cancel');
};
</script>

<style scoped>
.editable-field {
  display: inline-block;
  width: 100%;
}

.editable-cell {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.editable-cell:hover {
  background-color: #f3f4f6;
}

.editing-cell {
  display: inline-block;
  width: 100%;
}

.edit-input {
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  width: 100%;
  min-width: 150px;
  box-sizing: border-box;
}

.edit-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>
